// ** React Imports
import { forwardRef, useState, useRef, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";

// ** Custom Components
import Tooltip from "components/layout/Tooltip";
import { ConditionBlock } from "components/conditional";

// ** Third Party Components
import { toast } from "helpers/toastWrapper";

// ** Editor Components
import { useMentionContext, MentionLayer } from "./editor/mention";

// ** Custom Hooks
import useDetectKeyboardOpen from "hooks/useDetectKeyboardOpen";
import useGetTextLimitMessages from "hooks/useGetTextLimitMessages";

// ** Contexts
import deviceTypeContext from "contexts/deviceTypeContext";

// ** Utils & Helpers
import { isFunction } from "utils/isFunction";

import {
	ContentEditable,
	PlaceHolder,
	Container,
	EditorWrapper,
	DisabledOverlay,
	useEditor,
} from "./editor";

import { InnerButtons, OuterButtons } from "./button";

// #####################################################

export function isTargetOf(target, element) {
	if (target === element) {
		return true;
	}

	if (element.parentElement == null) {
		return false;
	}

	return isTargetOf(target, element.parentElement);
}

// #####################################################

const TextEditor = ({
	reference,
	key,
	disabled,
	initValue,
	withMentions = true,
	autoFocus: focus = false,
	button,
	buttonCancel,
	additional,
	placeholder,
	// TODO(Przemek): Properties that are going to be deprecated:
	singleLine: inline,
	// tooltipPlacement, // Refactor it to use a tooltip object options
	noEmoji,
	maxTextLength,
	tooltipInfo,
	linkOnButtonToPayments,
	messageSendingInProgress,
	aboutMe = false,
	inModal = false, // ustawić True dla edytora w modalu
	autoPositioning = false,
	noFullHeight, // wyłączenie 100% wysokości w miejscach gdzie przekazany jest button poprzez edytor i nie jest inline
	customMaxHeight,
	innerHeader = false,
}) => {
	const { isPhone } = useContext(deviceTypeContext);
	const { isKeyboardOpen } = useDetectKeyboardOpen();

	const history = useHistory();

	const [padding, setPadding] = useState(0);
	const [lastElementMargin, setLastElementMargin] = useState(0);
	const [isFocused, setIsFocused] = useState(false);
	const [customMarginRight, setCustomMarginRight] = useState(null);
	const [haveContent, setHaveContent] = useState(false);

	const placeholderRef = useRef(null);

	const { translateMessage, redirectToSuperVip } = useGetTextLimitMessages();

	const mention = useMentionContext({
		enabled: withMentions,
	});

	const action = useRef(null);

	action.current = button;

	const editor = useEditor({
		// ** Optional reference to this editor. Exports API access to the editor.
		reference,
		// ** Basic options.
		maxTextLength,
		// **  Settings this to true, will result in auto focusing the editor.
		focus,
		// ** Initial value.
		content: initValue,
		// ** Passing current disabled value.
		disabled,
		// ** If smartphone's keyboard covers the text editor.
		autoPositioning,
		// **  Adding custom keyboard bindings.
		bindings: {
			enter: (event) => {
				if (event.key !== "Enter") {
					return false;
				}

				if (event.key === "Enter" && event.shiftKey) {
					return false;
				}

				if (
					!inline ||
					!action.current ||
					!isFunction(action.current.onClick)
				) {
					return false;
				}

				event.preventDefault();

				const { plain, parsed } = editor.current.serialize();

				action.current.onClick(event, plain, parsed);

				return true;
			},
		},
		// **  Attaching context to editor.
		context: {
			mention,
		},
		// ** Handling resized components in the editor and applying changes if necessary.
		handleResized({ root, container, spacing }) {
			if (!root || !container || !spacing) {
				setPadding(0);
				return;
			}
			const editorRef = editor?.internal?.contenteditable?.current;
			if (editorRef) {
				setTimeout(() => {
					// true = editor has scroll
					if (editorRef?.scrollHeight > editorRef?.clientHeight) {
						setCustomMarginRight((prev) => {
							if (prev !== 18) {
								return 18;
							} else return prev;
						});
					} else {
						setCustomMarginRight((prev) => {
							if (prev !== 0) {
								return 0;
							} else return prev;
						});
					}

					setHaveContent((prev) => {
						const contentLength = editorRef?.innerText
							?.replace("\n", "") // removed new line sign
							?.replace(/\u200B/g, "")?.length; // next removed zero-width space

						const newHaveContent = Boolean(contentLength);
						return newHaveContent !== prev ? newHaveContent : prev;
					});
				}, 0);

				if (spacing.width > 0) {
					if (editorRef?.scrollHeight > editorRef?.clientHeight) {
						setLastElementMargin(spacing.width);
						setPadding(0);
					} else {
						setPadding(spacing.height);
					}
				}
			}

			const placeholderWidth = placeholderRef?.current?.offsetWidth || 0;
			const spacingForPlaceholderFinal = Math.max(
				container.width,
				placeholderWidth
			);

			if (root.width > spacingForPlaceholderFinal + spacing.width) {
				setPadding(0);
				return;
			}
		},
		// **  Basic event handlers exposed for updating react state.
		onFocus() {
			setIsFocused(true);
		},

		onBlur() {
			setIsFocused(false);
		},
	});

	useEffect(() => {
		if (disabled) {
			editor.current.blur();
			editor.internal.contenteditable.current.setAttribute(
				"contenteditable",
				"false"
			);

			return;
		}

		editor.internal.contenteditable.current.setAttribute(
			"contenteditable",
			"true"
		);
	}, [disabled]);

	useEffect(() => {
		if (isKeyboardOpen && isFocused && isPhone) {
			editor.internal.container.current.scrollIntoView({
				behavior: "instant",
				block: "center",
			});
		}
	}, [isKeyboardOpen, isFocused]);

	// #####################################################

	return (
		<Container ref={editor.internal.container} key={key}>
			<EditorWrapper
				inline={inline}
				isDisabled={disabled}
				focused={isFocused}
				$innerHeader={innerHeader}
				style={{
					paddingBottom: padding ? `${padding}px` : 0,
				}}
			>
				{innerHeader && innerHeader}
				<ContentEditable
					onFocus={(e) => {
						// Prevents focus when clicking outside the editor but close to it.
						if (
							window.lastClickTarget &&
							!editor.internal.container.current.contains(
								window.lastClickTarget
							) &&
							!window.lastClickTarget.classList.contains(
								"emojiModal" &&
									!window.lastClickTarget.classList.contains(
										"editor-paragraf"
									)
							)
						) {
							e.currentTarget.blur();
						}
					}}
					isDisabled={disabled}
					ref={editor.internal.contenteditable}
					tabindex="-1"
					innerButtonsWidth={lastElementMargin}
					focused={isFocused}
					style={
						messageSendingInProgress
							? {
									caretColor: "transparent",
							  }
							: null
					}
					minWidthParagraph={
						editor?.internal?.spacing?.current?.offsetWidth
					}
					customMaxHeight={customMaxHeight}
					$innerHeader={innerHeader}
				/>
				<MentionLayer
					editor={editor.current}
					context={mention}
					editorHeight={
						editor?.internal?.contenteditable?.current?.offsetHeight
					}
					innerRef={editor.internal.container}
					editorInternal={editor.internal}
					inModal={inModal}
				/>
				<PlaceHolder
					ref={placeholderRef}
					$showPlaceholder={
						placeholder &&
						!disabled &&
						!messageSendingInProgress &&
						!haveContent
					}
					$innerHeader={innerHeader}
				>
					{placeholder}
				</PlaceHolder>
				<InnerButtons
					key="inner-buttons"
					container={editor.internal.spacing}
					editor={editor}
					additional={additional}
					action={action.current}
					actionCancel={buttonCancel}
					useEmoji={!noEmoji}
					inline={inline}
					disabled={disabled}
					tooltipInfo={tooltipInfo}
					messageSendingInProgress={messageSendingInProgress}
					noFullHeight={noFullHeight}
					customMarginRight={
						customMarginRight >= 0 && `${customMarginRight}px`
					}
				/>

				<ConditionBlock value={disabled || messageSendingInProgress}>
					<DisabledOverlay
						aboutMe={aboutMe}
						linkOnButtonToPayments={linkOnButtonToPayments}
						onClick={() => {
							if (linkOnButtonToPayments) {
								toast.error(translateMessage);
								window.scrollTo(0, 0);
								history.push({
									pathname: `/userpanel/payments/buy`,
									state: {
										showSuperVip: redirectToSuperVip,
									},
								});
								return;
							}
						}}
					>
						<Tooltip
							text={
								linkOnButtonToPayments
									? translateMessage
									: tooltipInfo || ""
							}
							wrapperStyle={{
								position: "absolute",
								left: 0,
								right: 0,
								top: 0,
								bottom: 0,
							}}
						/>
					</DisabledOverlay>
				</ConditionBlock>
			</EditorWrapper>

			<OuterButtons
				key="outer-buttons"
				editor={editor}
				additional={additional}
				action={action.current}
				actionCancel={buttonCancel}
				inline={inline}
				disabled={disabled}
				tooltipInfo={tooltipInfo}
				container={editor.internal.container}
				noFullHeight={noFullHeight}
			/>
		</Container>
	);
};

// #####################################################

export default forwardRef((props, ref) => (
	<TextEditor reference={ref} {...props} />
));

// #####################################################
